import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { forkJoin, map, Observable, of } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import { CredentialStore } from "../../authentication/data-access/credential.store";
import {
    Contact,
    ContactPage,
    ContactPresence,
} from "../models/contact.models";

interface CallableContactResponse {
    userId: string;
    username: string;
    name: string;
    clientId: string;
    tags?: string[];
    profilePicture?: string | null;
    mobileUser?: boolean;
    status?: string;
}
interface CallableResponse {
    online?: CallableContactResponse[];
    sleeping?: CallableContactResponse[];
}
interface DirectoryUserResponse {
    _id?: string;
    gmvid?: string;
    clientId?: string;
    username: string;
    userDetails?: {
        displayName?: string;
        fullName?: string;
        firstName?: string;
        familyName?: string;
        tags?: string[];
        profilePicture?: string | null;
    };
}
interface DirectoryResponse {
    users?: DirectoryUserResponse[];
    nextpagetoken?: string | null;
}

@Injectable({ providedIn: "root" })
export class ContactsApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly credentials = inject(CredentialStore);

    load(
        onlineCursor?: string | null,
        sleepingCursor?: string | null,
        directoryCursor?: string | null
    ): Observable<ContactPage> {
        const callable$ =
            onlineCursor === null && sleepingCursor === null
                ? of({
                      contacts: [] as Contact[],
                      onlineCursor: null,
                      sleepingCursor: null,
                  })
                : this.loadCallable(onlineCursor, sleepingCursor);
        const directory$ =
            directoryCursor === null
                ? of({ contacts: [] as Contact[], directoryCursor: null })
                : this.loadDirectory(directoryCursor);
        return forkJoin({ callable: callable$, directory: directory$ }).pipe(
            map(({ callable, directory }) => ({
                contacts: [...directory.contacts, ...callable.contacts],
                onlineCursor: callable.onlineCursor,
                sleepingCursor: callable.sleepingCursor,
                directoryCursor: directory.directoryCursor,
            }))
        );
    }

    private loadCallable(
        onlineCursor?: string | null,
        sleepingCursor?: string | null
    ): Observable<Omit<ContactPage, "directoryCursor">> {
        let params = new HttpParams();
        if (onlineCursor) params = params.set("o_after", onlineCursor);
        if (sleepingCursor) params = params.set("s_after", sleepingCursor);
        let headers = new HttpHeaders({
            "Cache-Control": "no-cache",
            "x-gem-api-key": this.config.apiKey ?? "",
        });
        const token = this.credentials.readTokens()?.accessToken;
        if (token) headers = headers.set("x-auth-token", token);
        if (this.config.webVersion)
            headers = headers.set("x-version-web", this.config.webVersion);
        const signalingBase =
            this.config.realtimeBaseUrl +
            this.config.realtimePath.replace(/\/socket\.io\/?$/, "");
        return this.http
            .get<CallableResponse>(`${signalingBase}/user/callable`, {
                params,
                headers,
            })
            .pipe(
                map((response) => {
                    const online = response.online ?? [];
                    const sleeping = response.sleeping ?? [];
                    const activeOnline = onlineCursor === null ? [] : online;
                    const activeSleeping =
                        sleepingCursor === null ? [] : sleeping;
                    return {
                        contacts: [
                            ...activeOnline.map((contact) =>
                                this.mapContact(contact)
                            ),
                            ...activeSleeping.map((contact) =>
                                this.mapContact(contact, "offline")
                            ),
                        ],
                        onlineCursor:
                            onlineCursor === null
                                ? null
                                : activeOnline.length
                                  ? activeOnline.at(-1)!.username
                                  : null,
                        sleepingCursor:
                            sleepingCursor === null
                                ? null
                                : activeSleeping.length
                                  ? activeSleeping.at(-1)!.username
                                  : null,
                    };
                })
            );
    }

    private loadDirectory(
        directoryCursor?: string | null
    ): Observable<{ contacts: Contact[]; directoryCursor: string | null }> {
        let params = new HttpParams();
        if (directoryCursor)
            params = params.set("nextpagetoken", directoryCursor);
        return this.http
            .get<DirectoryResponse>(
                `${this.config.apiBaseUrl}/servauthen/user`,
                { params }
            )
            .pipe(
                map((response) => ({
                    contacts: (response.users ?? []).map((user) => {
                        const details = user.userDetails ?? {};
                        const name =
                            details.displayName ||
                            details.fullName ||
                            [details.firstName, details.familyName]
                                .filter(Boolean)
                                .join(" ") ||
                            user.username;
                        return {
                            id: user.gmvid || user._id || user.username,
                            username: user.username,
                            name,
                            clientId: user.clientId ?? "",
                            tags: details.tags ?? [],
                            profilePicture: details.profilePicture ?? null,
                            mobileUser: false,
                            presence: "offline" as const,
                        };
                    }),
                    directoryCursor: response.nextpagetoken || null,
                }))
            );
    }

    private mapContact(
        contact: CallableContactResponse,
        fallback: ContactPresence = "online"
    ): Contact {
        const presence =
            contact.status === "unavailable"
                ? "busy"
                : contact.status === "offline" || contact.status === "sleeping"
                  ? "offline"
                  : fallback;
        return {
            id: contact.userId || contact.username,
            username: contact.username,
            name: contact.name || contact.username,
            clientId: contact.clientId,
            tags: contact.tags ?? [],
            profilePicture: contact.profilePicture ?? null,
            mobileUser: Boolean(contact.mobileUser),
            presence,
        };
    }
}
